<template>
  <h3 class="font-title font-bold text-h3 text-secondary mb-6">{{ labels.title }}</h3>

  <form @submit.prevent="submit">
    <div class="mb-4">
      <input
        type="text"
        :placeholder="labels.name_placeholder"
        v-model="form.name"
        class="w-full border border-neutral-light rounded-lg px-4 py-3 text-p2 text-neutral-dark focus:outline-none focus:border-primary"
      />
      <span v-if="errors.name" class="block mt-1 text-xs text-secondary">{{ errors.name }}</span>
    </div>

    <div class="mb-4">
      <input
        type="text"
        :placeholder="labels.phone_placeholder"
        v-model="form.phone"
        class="w-full border border-neutral-light rounded-lg px-4 py-3 text-p2 text-neutral-dark focus:outline-none focus:border-primary"
      />
      <span v-if="errors.phone" class="block mt-1 text-xs text-secondary">{{ errors.phone }}</span>
    </div>

    <div class="mb-4">
      <input
        type="email"
        :placeholder="labels.email_placeholder"
        v-model="form.email"
        class="w-full border border-neutral-light rounded-lg px-4 py-3 text-p2 text-neutral-dark focus:outline-none focus:border-primary"
      />
      <span v-if="errors.email" class="block mt-1 text-xs text-secondary">{{ errors.email }}</span>
    </div>

    <div class="mb-6 relative">
      <input
        type="date"
        v-model="form.date"
        class="w-full border border-neutral-light rounded-lg px-4 py-3 text-p2 text-neutral-dark focus:outline-none focus:border-primary pr-10"
      />
      <i class="ph ph-calendar-blank absolute right-4 top-1/2 -translate-y-1/2 text-secondary text-lg pointer-events-none"></i>
      <span v-if="errors.date" class="block mt-1 text-xs text-secondary">{{ errors.date }}</span>
    </div>

    <button
      type="submit"
      :disabled="loading"
      class="w-full bg-primary-dark text-white font-title font-bold text-p2 px-8 py-3 rounded-xl hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary-dark focus:ring-offset-2 transition disabled:opacity-50"
    >
      {{ loading ? labels.sending : labels.submit }}
    </button>

    <div v-if="success" class="mt-4 text-green-500 font-semibold">{{ labels.success_message }}</div>
    <div v-if="serverError" class="mt-4 text-secondary font-semibold">{{ serverError }}</div>
  </form>
</template>

<script setup>
import { ref, reactive } from 'vue';
import client from '../http/client.js';

const props = defineProps({
  labels: { type: Object, default: () => ({}) },
  propertyId: { type: [Number, String], required: true },
  locale: { type: String, default: 'es' },
});

const form = reactive({
  name: '',
  phone: '',
  email: '',
  date: '',
});

const errors = reactive({});
const loading = ref(false);
const success = ref(false);
const serverError = ref('');

function validate() {
  Object.keys(errors).forEach((k) => delete errors[k]);

  if (!form.name.trim()) errors.name = props.labels.name_required ?? 'Name is required';
  if (!form.phone.trim()) errors.phone = props.labels.phone_required ?? 'Phone is required';
  if (!form.email.trim()) errors.email = props.labels.email_required ?? 'Email is required';
  else if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.email = props.labels.email_invalid ?? 'Email is invalid';
  if (!form.date.trim()) errors.date = props.labels.date_required ?? 'Date is required';

  return Object.keys(errors).length === 0;
}

async function submit() {
  serverError.value = '';
  success.value = false;

  if (!validate()) return;

  loading.value = true;

  try {
    const response = await client.post(`/api/properties/${props.propertyId}/tour-request`, {
      name: form.name,
      email: form.email,
      phone: form.phone,
      date: form.date,
      locale: props.locale,
    });

    if (response.data?.success) {
      success.value = true;
      form.name = '';
      form.phone = '';
      form.email = '';
      form.date = '';
    }
  } catch (error) {
    if (error.response?.status === 422 && error.response.data?.errors) {
      const serverErrors = error.response.data.errors;
      Object.keys(serverErrors).forEach((key) => {
        errors[key] = serverErrors[key][0];
      });
    } else {
      serverError.value = error.response?.data?.message || props.labels.error_message || 'An error occurred';
    }
  } finally {
    loading.value = false;
  }
}
</script>
